import {
  ClipboardCheck,
  Car,
  CarFront,
  Droplets,
  Cog,
  Cpu,
  ArrowUpRight,
} from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";

const SERVICES = [
  {
    title: "Pre Delivery Inspection (PDI)",
    desc: "Buying a brand-new car? We inspect it before delivery so hidden manufacturing defects don't become your problem.",
    icon: ClipboardCheck,
    tag: "New Cars",
    slug: "pdi",
  },
  {
    title: "Used Car Inspection",
    desc: "Complete health check before buying any second-hand vehicle.",
    icon: Car,
    tag: "Pre-Owned",
    slug: "used-car",
  },
  {
    title: "Accident Damage Inspection",
    desc: "Detect repaired panels, structural repairs and hidden accident history.",
    icon: CarFront,
    tag: "Body & Frame",
    slug: "accident",
  },
  {
    title: "Flood Damage Inspection",
    desc: "Identify water-damaged vehicles before making a purchase.",
    icon: Droplets,
    tag: "Electrical",
    slug: "flood",
  },
  {
    title: "Engine & Transmission",
    desc: "Mechanical health check with detailed findings.",
    icon: Cog,
    tag: "Mechanical",
    slug: "engine",
  },
  {
    title: "OBD Computer Diagnostics",
    desc: "Professional scanner-based fault code inspection.",
    icon: Cpu,
    tag: "Electronics",
    slug: "obd",
  },
];

export const Services = ({ onBook }) => (
  <section id="services" data-testid="services-section" className="py-28 md:py-36">
    <div className="mx-auto max-w-7xl px-5 md:px-8">
      <Reveal>
        <Chapter index="01" label="Services" />
      </Reveal>
      <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
        <Reveal delay={0.08}>
          <h2 className="max-w-2xl font-heading text-4xl font-extrabold tracking-tight text-white md:text-6xl">
            Every car hides a story.
            <span className="block text-white/40">We read it first.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="max-w-sm text-sm leading-relaxed text-white/50 md:text-base">
            Six specialised inspections, one goal — make sure you never pay for
            someone else's mistake.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={0.06 * i}>
            <article
              data-testid={`service-card-${s.slug}`}
              className="group relative h-full rounded-3xl border border-white/10 bg-white/[0.03] p-8 transition-[transform,border-color,background-color] duration-500 hover:-translate-y-2 hover:border-gold/40 hover:bg-white/[0.05]"
            >
              <div className="flex items-start justify-between">
                <span className="grid h-13 w-13 place-items-center rounded-2xl border border-gold/25 bg-gold/10 p-3.5 text-gold transition-transform duration-500 group-hover:scale-110">
                  <s.icon className="h-6 w-6" strokeWidth={1.8} />
                </span>
                <span className="rounded-full border border-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">
                  {s.tag}
                </span>
              </div>
              <h3 className="mt-7 font-heading text-xl font-extrabold tracking-tight text-white">
                {s.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/55">
                {s.desc}
              </p>
              <button
                data-testid={`service-book-${s.slug}`}
                onClick={() => onBook(s.title)}
                className="mt-7 flex items-center gap-2 text-sm font-bold text-gold transition-colors duration-300 hover:text-gold-light"
              >
                Book this inspection
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
